import { SearchMentorResponse } from './../../models/account/mentor/searchMentorResponse';
import { CalendlyService } from './../../services/calendly/calendly.service';
import { Component } from '@angular/core';
import { SkeletonModule } from 'primeng/skeleton';
import { CalendarModule } from 'primeng/calendar';
import { InputTextModule } from 'primeng/inputtext';
import { DropdownModule } from 'primeng/dropdown';
import { ButtonModule } from 'primeng/button';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { CommonModule } from '@angular/common';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { ToastModule } from 'primeng/toast';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { IntegrateCalenderlyComponent } from '../integrate-calenderly/integrate-calenderly.component';
import { ActivatedRoute, Router } from '@angular/router';
import { MentorsSearchFilterComponent } from "./mentors-search-filter/mentors-search-filter.component";
import { MentorService } from '../../services/account/mentor.service';
import { SearchMentorRequest } from '../../models/account/mentor/searchMentorRequest';
import { SuperAdminService } from '../../services/super-admin/super-admin.service';
import { loginResponse } from '../../models/account/loginResponse';
import { DecodedToken } from '../../guards/permission.guard';
import { jwtDecode } from 'jwt-decode';
import { AuthRoles, AuthRolesDescriptions } from '../../models/enums/enums';

@Component({
  selector: 'app-mentors-list',
  standalone: true,
  imports: [SkeletonModule, CalendarModule, ProgressSpinnerModule, DialogModule, InputTextModule, DropdownModule, ButtonModule, TableModule, CommonModule, ToastModule, ConfirmDialogModule, IntegrateCalenderlyComponent, MentorsSearchFilterComponent],
  templateUrl: './mentors-list.component.html',
  styleUrl: './mentors-list.component.scss',
  providers: [ConfirmationService, MessageService]
})
export class MentorsListComponent {
  isCalendlyIntegrated: boolean = false;
  isLoading: boolean = false;
  isOperations: boolean = false;

  constructor(private messageService: MessageService,
    private calendlyService: CalendlyService, private route: ActivatedRoute, private router: Router,
    private mentorService: MentorService, private superAdminService: SuperAdminService) { }


  ngOnInit() {
    this.isLoading = true;
    this.checkIfOperations();
    this.getCalendlyIntegrationStatus();

    this.route.queryParamMap.subscribe(params => {
      const code = params.get('code');
      if (code) {
        this.saveIntegrationStatus(code);
      }
    });
  }

  checkIfOperations() {
    const accountStr = localStorage.getItem('account');
    if (accountStr) {
      try {
        const accountObj = JSON.parse(accountStr);
        if (accountObj && accountObj.token) {
          const decodedToken: DecodedToken = jwtDecode<DecodedToken>(accountObj.token);
          const rolesClaim = decodedToken['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];
          const roles: string[] = Array.isArray(rolesClaim) ? rolesClaim : rolesClaim ? [rolesClaim] : [];
          if (roles.includes(AuthRolesDescriptions[AuthRoles.Operations])) {
            this.isOperations = true;
            this.mentorSearchRequest.state = 1; 
          }
        }
      } catch (e) {
        console.warn('Error parsing account from local storage', e);
      }
    }
  }

  isSearching: boolean = false;
  mentorSearchRequest: SearchMentorRequest = {
    mentorName:null,
    companyOrSchool: null,
    totalExperience:null,
    nationality:null,
    email: null,
    startDate: null,
    state: null,
    endDate: null,
    pageNumber: 1,
    pageSize: 10
  };
  mentors: SearchMentorResponse[] = [];
  total: number = 0;
  first: number = 0;
  rowSize: number = 10;

  isAccepting: boolean = false;
  isRejecting: boolean = false;
  isResendingInvite: boolean = false;

  isVisible:boolean = false;
  selectedMentorId:string = '';

  saveIntegrationStatus(code: string) {
    this.isLoading = true;
    this.calendlyService.saveIntegrationStatus(code).subscribe(
      {
        next: (res) => {
          this.isCalendlyIntegrated = true;
          this.isLoading = false;
          this.router.navigate([], { queryParams: { code: null }, queryParamsHandling: 'merge' });
        },
        error: (err) => {
          console.warn('Error in saving calendly integration status', err);
          this.isLoading = false;
        }
      });
  }

  getCalendlyIntegrationStatus() {
    this.calendlyService.isIntegrationExists().subscribe(
      {
        next: (res) => {
          this.isCalendlyIntegrated = res;
          if (this.isCalendlyIntegrated) {
            this.searchMentors(1);
          }
          this.isLoading = false;
        },
        error: (err) => {
          console.warn('Error in getting calendly integration status', err);
          this.isLoading = false;
        }
      });
  }

  resendInvite() {
    this.isResendingInvite = true;
    this.mentorService.sendCalendlyInvite(this.selectedMentorId).subscribe({
      next: (response) => {
        this.isResendingInvite = false;
        this.isVisible=false;
        this.messageService.add({ severity: 'info', summary: 'Confirmed', detail: 'Mentor has been invited', life: 3000 });
      },
      error: (error) => {
        this.isResendingInvite = false;
        this.isVisible=false;
        this.messageService.add({
          severity: 'error',
          summary: 'Failed to Send Invitation',
          detail: 'An error occurred while sending the invite. Please try again.',
          life: 3000
        });      }
    });
  }

  cancel() {
    this.isVisible=false;
  }
  calculateTotalExperience(experience: any): string | null {
    if (!experience || (!experience.years && !experience.months)) {
      return null;
    }
    // Ensure that years and months are valid numbers, defaulting to 0 if they are not
    const years = Number(experience.years?.name) || 0;
    const months = Number(experience.months?.name) || 0;
    return (years + (months / 12)).toFixed(2);
  }

  handleSearchOutput(data: any) {
    const experience = this.calculateTotalExperience(data.totalExperience)
    this.mentorSearchRequest = {
      mentorName: data.name,
      companyOrSchool: data.companyOrSchool,
      totalExperience:experience,
      nationality:data.nationality,
      email: data.email,
      startDate: data.fromDate,
      state: this.isOperations ? 1 : data.status,
      endDate: data.toData,
      pageNumber: 1,
      pageSize: 10
    }
    this.searchMentors(1);
    
    // You can now perform any other actions with the received data here
  }

  searchMentors(pageNumber: Number) {
    this.isSearching = true;
    this.mentorSearchRequest.pageNumber = pageNumber;
    this.mentorSearchRequest.pageSize = this.rowSize;
    this.mentorService.searchMentor(this.mentorSearchRequest).subscribe({
      next: (response) => {
        this.mentors = response.result ?? [];
        this.total = response.total ?? 0;
        this.isSearching = false;
      },
      error: (error) => {
        this.isSearching = false;
        console.warn('Error in searching mentors', error);
      }
    });
  }

  onLazyLoad($event: TableLazyLoadEvent): void {
    const pageNumber = Math.floor(($event.first ?? 0) / ($event.rows ?? this.rowSize)) + 1;
    this.first = $event.first ?? 0;
    this.rowSize = $event.rows ?? this.rowSize;
    this.searchMentors(pageNumber);
  }

  openResume(arg0: string) {
    window.open(arg0, '_blank');
  }

  loginAsMentor(mentorId: string){
    this.isLoading = true;
    this.superAdminService.loginAsMentorFromSuperAdmin(mentorId).subscribe({
      next: (response: loginResponse) => {
        if (response) {
          const decodedToken: DecodedToken = jwtDecode<DecodedToken>(response.token);
          const rolesClaim = decodedToken['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];
          const roles: string[] = Array.isArray(rolesClaim) ? rolesClaim : rolesClaim ? [rolesClaim] : [];
          if (roles.some(role => role == AuthRolesDescriptions[AuthRoles.Mentor])) {
            localStorage.setItem("account", JSON.stringify(response));
            this.router.navigate(['/mentor-portal']); // Adjust the path as needed
          }
          else {
            this.messageService.add({ severity: 'error', summary: 'Unauthorized', detail: 'You are not authorized to continue' });
          }
        }
        this.isLoading = false;
      },
      error: (error) => {
        this.messageService.add({ severity: 'error', summary: error.name, detail: error.message });
        this.isLoading = false;
      },
      complete: () => {
        this.isLoading = false;
      }
    })
  }

  rejectMentorRequest(mentorId: string,index:number) {
    this.isRejecting = true;
    this.mentorService.updateMentorState({ mentorId: mentorId, mentorState: 2 }).subscribe({
      next: (response) => {
        this.isRejecting = false;
        this.mentors[index].state = 2; // Update the mentor state to "Rejected"
        this.messageService.add({ severity: 'info', summary: 'Confirmed', detail: 'You have Rejected', life: 3000 });
      },
      error: (error: any) => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Error while processing request', life: 3000 });
        this.isRejecting = false;
      }
    })
  }

  acceptMentorRequest(mentorId: string,index:number) {
    this.isAccepting = true;
    this.mentorService.updateMentorState({ mentorId: mentorId, mentorState: 1 }).subscribe({
      next: (response) => {
        this.isAccepting = false;
        this.mentors[index].state = 1; // Update the mentor state to "Accepted"
        this.messageService.add({ severity: 'info', summary: 'Confirmed', detail: 'You have accepted', life: 3000 });
      },
      error: (error: any) => {
        if (error.message == 'The process is not compeleted to continue') {
          this.isVisible = true;
          this.selectedMentorId=mentorId;
        }
        else {
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Error while processing request', life: 3000 });
        }
        this.isAccepting = false;
      }
    })
  }
}
